import type { DiscoveredProject } from "../../core/discovery";

/** The dropdown entry that turns into a free field. */
export const NEW = "__new__";
/** «Nessun sottospazio»: the project sits directly in the space. */
export const NONE = "__none__";

const same = (a: string, b: string) => a.toLocaleLowerCase() === b.toLocaleLowerCase();

/**
 * `space/project` or `space/subspace/project`, as proposed or saved. One segment is a bare
 * project; the subspace is `null` when the proposal doesn't name one.
 */
export const splitScope = (
  scope: string,
): { space: string; subspace: string | null; project: string } => {
  const parts = scope
    .split("/")
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length === 0) return { space: "", subspace: null, project: "" };
  if (parts.length === 1) return { space: "", subspace: null, project: parts[0] as string };
  if (parts.length === 2)
    return { space: parts[0] as string, subspace: null, project: parts[1] as string };
  return {
    space: parts[0] as string,
    subspace: parts[1] as string,
    project: parts.slice(2).join("/"),
  };
};

/** The proposal the three fields add up to, or "" while one is missing. */
export const composeScope = (space: string, subspace: string, project: string): string =>
  space.trim() && project.trim()
    ? [space.trim(), subspace.trim(), project.trim()].filter(Boolean).join("/")
    : "";

export type PickerSeed = {
  space: string;
  spaceText: string;
  /** "" = not chosen yet, `NONE`, `NEW` or a subspace name. */
  subspace: string;
  subspaceText: string;
  project: string;
  projectText: string;
};

/**
 * What the fields start from, given the saved proposal and what Flow offers. Names are
 * matched without minding upper and lower case (`work/helios` finds `Work`), and a proposal
 * that doesn't name the subspace still finds its project when the space has only one with
 * that name (`work/helios` → `Work/Applicazioni/helios`). What Flow doesn't know yet goes in
 * the free fields.
 */
export const seedPicker = (initial: string, projects: DiscoveredProject[]): PickerSeed => {
  const asked = splitScope(initial);
  const space = projects.find((x) => same(x.space, asked.space))?.space;
  if (!space) {
    return {
      space: asked.space ? NEW : "",
      spaceText: asked.space,
      subspace: asked.subspace ? NEW : asked.space ? NONE : "",
      subspaceText: asked.subspace ?? "",
      project: asked.project ? NEW : "",
      projectText: asked.project,
    };
  }
  const inSpace = projects.filter((x) => x.space === space);
  const matches = inSpace.filter(
    (x) =>
      same(x.project, asked.project) &&
      (asked.subspace === null || same(x.subspace ?? "", asked.subspace)),
  );
  if (matches.length === 1) {
    const [found] = matches as [DiscoveredProject];
    return {
      space,
      spaceText: "",
      subspace: found.subspace ?? NONE,
      subspaceText: "",
      project: found.project,
      projectText: "",
    };
  }
  const knownSubspace =
    asked.subspace === null
      ? undefined
      : inSpace.find((x) => x.subspace && same(x.subspace, asked.subspace as string))?.subspace;
  return {
    space,
    spaceText: "",
    subspace: asked.subspace === null ? NONE : (knownSubspace ?? NEW),
    subspaceText: asked.subspace !== null && !knownSubspace ? asked.subspace : "",
    project: asked.project ? NEW : "",
    projectText: asked.project,
  };
};

/** The subspaces of a space, sorted, without the «none». */
export const subspacesOf = (projects: DiscoveredProject[], space: string): string[] =>
  [
    ...new Set(
      projects.filter((p) => p.space === space && p.subspace).map((p) => p.subspace as string),
    ),
  ].sort((a, b) => a.localeCompare(b));

/** The projects a space + subspace choice offers (`NONE` = directly in the space). */
export const projectsIn = (
  projects: DiscoveredProject[],
  space: string,
  subspace: string,
): DiscoveredProject[] =>
  projects.filter(
    (p) => p.space === space && (subspace === NONE ? !p.subspace : p.subspace === subspace),
  );
